import * as Sentry from '@sentry/react-native';
import dayjs from 'dayjs';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Dimensions,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  PanResponder,
  ListRenderItemInfo,
  View,
} from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import CachedImage from '@components/CachedImage/CachedImage';
import { CachedVideo } from '@components/CachedImage/CachedVideo';
import TagsCollection from '@components/Chat/TagsCollection.web';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { MediaShareModal } from '@components/Modals/MediaShareModal.web';
import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import {
  MarginLeftM,
  MarginRightM,
  MarginRightXxs,
} from '@components/shared/ListSpacers';
import { Document, useGetTaskQuery } from '@graphql/generated';
import { useDeleteProjectDocument } from '@hooks/useDeleteProjectDocument';
import { useDeleteTaskFiles } from '@hooks/useDeleteTaskFile';
import { usePreviewDocuments } from '@hooks/usePreviewDocuments';
import { isTaskFileViewerOnly } from '@hooks/useTaskMembership';
import theme from '@themes/theme';
import {
  fetchWithErrorHandling,
  HttpClientError,
} from '@utils/fetchWithErrorHandling';
import { AsyncStorage } from '@utils/storage';
import { generateTagCollections } from '@utils/tagCollectionService';

export const ImagesPreview: React.FC<{
  isGlobalSearch?: boolean;
  onDismiss?: () => void;
  fromChatMessage?: boolean;
  chatId?: string;
  isViewerOnly?: boolean;
  taskId?: string;
  supportNonImageFiles?: boolean;
  // Cursor pagination passthrough (TA-9771): when the caller's document list
  // is paginated, these let the viewer request more documents as the user
  // navigates toward the end of the loaded set.
  hasNextPage?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}> = ({
  isGlobalSearch = false,
  onDismiss,
  fromChatMessage = false,
  chatId: fromChatId = '',
  isViewerOnly = false,
  taskId: taskIdOfLastPage,
  supportNonImageFiles = false,
  hasNextPage = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const {
    activeIndex,
    setActiveIndex,
    previewDocuments = [],
    tagsCollections,
  } = usePreviewDocuments();
  // Deleting or paginating can leave activeIndex past the end of the list,
  // so every render-path read below must tolerate an undefined document
  // (TA-10454, Sentry FRONTEND-27M on the native twin).
  const activeDoc = previewDocuments[activeIndex];

  const screenWidth = Dimensions.get('window').width || window.innerWidth;
  const { t } = useTranslation('models');
  // delete files
  const { deleteProjectFileById } = useDeleteProjectDocument();
  const { deleteTaskFileById } = useDeleteTaskFiles();
  const [showDeleteTaskFile, setShowDeleteTaskFile] = React.useState(false);
  const [showDeleteProjectFile, setShowDeleteProjectFile] =
    React.useState(false);
  const taskDocs = previewDocuments?.[activeIndex]?.taskDocuments || [];
  const projectDocs = previewDocuments?.[activeIndex]?.projectDocuments || [];

  const messagedoc = previewDocuments?.[activeIndex]?.messageDocuments;
  const chatId = messagedoc?.map((item) => item.message?.chatId) || '';

  let deleteCall = fromChatMessage ? 'chat' : '';

  if ((!isGlobalSearch && taskDocs.length > 0) || taskIdOfLastPage) {
    deleteCall = 'task';
  } else if (!isGlobalSearch && projectDocs.length > 0) {
    deleteCall = 'project';
  }

  const taskId =
    taskDocs.length > 0
      ? (taskDocs[0].task?.id ?? taskIdOfLastPage)
      : taskIdOfLastPage;
  const { data: taskData } = useGetTaskQuery({
    fetchPolicy: 'cache-only',
    variables: {
      id: taskId,
    },
    skip: !taskId,
  });

  const { getTask: task } = taskData || {};
  let xisViewerOnly = isViewerOnly;
  if (!isViewerOnly && deleteCall === 'task' && task) {
    xisViewerOnly = isTaskFileViewerOnly(task);
  }
  const getTags = (idx: number) => {
    if (tagsCollections.length !== 0) return tagsCollections.flat();
    const item: Document = previewDocuments[idx];
    const list = [];
    item?.projectDocuments?.forEach((pd, index2) => {
      if (!list.some((x) => x.projectId === pd.project?.id)) {
        list.push({
          __typename: 'MessageProject',
          id: `${pd.project?.id}-${index2}`,
          projectId: pd.project?.id,
          authorId: item.owner?.id,
          author: item.owner,
          project: pd.project,
        });
      }
    });
    item?.taskDocuments?.forEach((td, index2) => {
      if (
        !list.some(
          (x) => x.projectId === td.task?.projectId && x.taskId === td.task?.id
        )
      ) {
        list.push({
          __typename: 'MessageTask',
          id: `${td.task?.id}-${index2}`,
          authorId: item.owner?.id,
          author: item.owner,
          projectId: td.task?.projectId,
          taskId: td.task?.id,
          task: td.task,
        });
      }
    });
    return generateTagCollections(list).flat();
  };
  const [tags, setTags] = useState(getTags(activeIndex));
  const [imgPercentage, setImgPercentage] = useState(100);
  const MAX_PERCENTAGE = 200;
  const MIN_PERCENTAGE = 10;

  // True while the user pressed "next" at the last loaded document and we are
  // waiting for the next page to arrive (TA-9771).
  const [pendingAdvance, setPendingAdvance] = useState(false);
  // Guards against firing onLoadMore repeatedly for the same loaded length —
  // fetchMore doesn't flip Apollo's `loading`, so the caller can't guard it.
  const lastRequestedLenRef = useRef(-1);

  // Pagination UI (arrow past the end, strip loading cell) is only meaningful
  // when the caller wired BOTH flags — hasNextPage without onLoadMore would
  // render controls that can never load anything.
  const canLoadMore = hasNextPage && !!onLoadMore;

  const requestMore = (force = false) => {
    if (!canLoadMore || loadingMore) return;
    if (!force && lastRequestedLenRef.current === previewDocuments.length)
      return;
    lastRequestedLenRef.current = previewDocuments.length;
    onLoadMore?.();
  };

  const navigateTo = (index: number) => {
    if (index < 0) return;
    if (index >= previewDocuments.length) {
      // Past the loaded page: request the next page and advance once it lands.
      // Force past the length guard so an explicit press retries a fetch that
      // previously failed for this length.
      if (canLoadMore) {
        if (!pendingAdvance) requestMore(true);
        setPendingAdvance(true);
      }
      return;
    }
    // Landing on an in-range index cancels any pending page-advance so the
    // arrow spinner clears and a later-arriving page doesn't yank the user
    // away from where they navigated.
    setPendingAdvance(false);
    setImgPercentage(100);
    setOffset({ x: 0, y: 0 });
    setActiveIndex(index);
    setTags(getTags(index));
  };

  // Advance onto the first newly loaded document once the pending page lands.
  useEffect(() => {
    if (!pendingAdvance) return;
    if (activeIndex < previewDocuments.length - 1) {
      setPendingAdvance(false);
      navigateTo(activeIndex + 1);
    } else if (!canLoadMore) {
      setPendingAdvance(false);
    }
  }, [pendingAdvance, previewDocuments.length, canLoadMore]);

  // Unstick: if the load settles (loadingMore back to false) without the list
  // growing — e.g. fetchMore rejected on a GraphQL error — drop the spinner
  // and re-arm the request guard so the next press retries instead of hanging
  // forever. The timer is cancelled whenever the deps change (new page landed
  // or a fresh load started).
  useEffect(() => {
    if (!pendingAdvance || loadingMore) return;
    const timer = setTimeout(() => {
      lastRequestedLenRef.current = -1;
      setPendingAdvance(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [pendingAdvance, loadingMore, previewDocuments.length]);

  // Prefetch the next page before the user hits the end of the loaded set so
  // the arrow press is instant in the common case (TA-9771).
  const PREFETCH_AHEAD = 5;
  useEffect(() => {
    if (!canLoadMore || loadingMore) return;
    if (previewDocuments.length - 1 - activeIndex <= PREFETCH_AHEAD) {
      requestMore();
    }
  }, [activeIndex, previewDocuments.length, canLoadMore, loadingMore]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (activeIndex < previewDocuments.length - 1 || canLoadMore) &&
        event.keyCode === 39
      ) {
        navigateTo(activeIndex + 1);
      } else if (activeIndex > 0 && event.keyCode === 37) {
        navigateTo(activeIndex - 1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, previewDocuments.length, canLoadMore]);

  // TA-10057: track whether the close was user-initiated (X button) so we can
  // distinguish expected closes from unexpected popstate dismissals in Sentry.
  const closedByUserRef = useRef(false);

  useEffect(() => {
    window.history.pushState({ imagePreviewOpen: true }, '');

    const handlePopState = () => {
      if (!closedByUserRef.current) {
        // Not triggered by the explicit close button — log for TA-10057 investigation.
        Sentry.addBreadcrumb({
          category: 'preview.close',
          message: 'ImagesPreview.web dismissed via unexpected popstate',
          level: 'warning',
        });
        Sentry.captureMessage(
          'TA-10057: ImagesPreview.web dismissed via unexpected popstate',
          'warning'
        );
      }
      closedByUserRef.current = false;
      onDismiss?.();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleClose = () => {
    closedByUserRef.current = true;
    window.history.back();
  };

  const { t: formatTime } = useTranslation('format');
  const [rotation, setRotation] = useState<{ [key: string]: number }>({});
  const [isModalVisible, setIsModalVisible] = useState(true);
  const videoRefs = useRef<{ [clientId: string]: HTMLVideoElement | null }>({});

  const rotateRight = () => {
    const current = previewDocuments[activeIndex];
    if (!current) return;

    const id = current.clientId;
    setRotation((prev) => ({
      ...prev,
      [id]: ((prev[id] ?? 0) + 90) % 360,
    }));
  };

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderMove: (_evt, gestureState) => {
        setOffset({
          x: offset.x + gestureState.dx,
          y: offset.y + gestureState.dy,
        });
      },
      onPanResponderRelease: (_evt, gestureState) => {
        setOffset({
          x: offset.x + gestureState.dx,
          y: offset.y + gestureState.dy,
        });
      },
    })
  ).current;

  const downloadImage = async () => {
    // here not called url, cdnurl can do the same.
    const image = previewDocuments[activeIndex];
    if (!image) return;
    let fileURL = image.file.url;

    if (!fileURL) {
      fileURL = (await AsyncStorage.getItem(image.clientId)) ?? '';
    }

    fetchWithErrorHandling(fileURL, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    })
      .then((resp) => resp.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        if (!url) return;
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = image.name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        const msg =
          err instanceof HttpClientError && err.status === 404
            ? t('files.downloadFileNotFound')
            : t('files.downloadNetworkError');
        alert(msg);
      });
  };

  useEffect(() => {
    setIsModalVisible(true);

    return () => {
      Sentry.addBreadcrumb({
        category: 'preview.unmount',
        message: 'ImagesPreview.web component unmounted',
        level: 'info',
      });
      setIsModalVisible(false);
      setTags([]);
      Object.values(videoRefs.current).forEach((videoRef) => {
        if (videoRef?.pause) {
          videoRef.pause();
        }
      });
    };
  }, []);

  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([clientId, videoRef]) => {
      if (videoRef && clientId !== previewDocuments[activeIndex]?.clientId) {
        videoRef.pause();
      }
    });
  }, [activeIndex, previewDocuments]);

  const carouselItem = useCallback(
    (item: Document) => {
      const { file } = item;
      const isImage = item.isImage || item.contentType?.includes('image/');
      const isVideo = item.isVideo || item.contentType?.includes('video');
      const rotationAngle = rotation[item?.clientId] ?? 0;

      if (isVideo) {
        return (
          <CachedVideo
            thumbnail={item?.file.thumbnail}
            videoRefs={videoRefs}
            clientId={item.clientId}
            screenWidth={screenWidth}
          />
        );
      } else if (!supportNonImageFiles || isImage) {
        return (
          <Box
            {...panResponder.panHandlers}
            width={screenWidth}
            height={window.innerHeight - 293}
            justifyContent='center'
            alignItems='center'
            style={{ overflow: 'hidden' }}>
            <View style={{ transform: [{ rotate: `${rotationAngle}deg` }] }}>
              <CachedImage
                imageClientId={item.clientId}
                image={file}
                height={window.innerHeight - 293}
                screenWidth={screenWidth}
                style={{
                  transform: [
                    { scale: imgPercentage / 100 },
                    { translateX: imgPercentage > 100 ? offset.x : 0 },
                    { translateY: imgPercentage > 100 ? offset.y : 0 },
                  ],
                  backgroundColor: theme.colors.black,
                }}
                resizeMode='contain'
              />
            </View>
          </Box>
        );
      } else {
        return (
          <Box
            {...panResponder.panHandlers}
            width={screenWidth}
            height={window.innerHeight - 293}
            alignItems='center'
            justifyContent='center'>
            <Icon
              fileType={(item as any).contentType || undefined}
              color='blue'
              variant='xl'
              marginBottom='l'
              width={240}
              height={240}
            />
            <Button
              width={200}
              height={56}
              isSmall
              backgroundColor='grey01'
              prefix={<Icon variant='l' name='Download' color='black' />}
              onPress={downloadImage}
              borderRadius='xs'
              variant='reversed'
              color='black'
              prefixMarginRight='xs'
              accessibilityLabel={t('models:projects.files.download')}>
              {t('models:projects.files.download')}
            </Button>
          </Box>
        );
      }
    },
    [
      rotation,
      screenWidth,
      isModalVisible,
      activeIndex,
      previewDocuments,
      imgPercentage,
      offset,
      supportNonImageFiles,
    ]
  );

  const thumbnailWidth = 160;
  const thumbnailHeight = 119;

  const renderThumbnailItem = ({
    item,
    index,
  }: ListRenderItemInfo<Document>) =>
    activeIndex !== index ? (
      <Box
        width={thumbnailWidth}
        height={thumbnailHeight}
        justifyContent='center'
        alignItems='center'
        pointerEvents='auto'>
        <TouchableOpacity
          accessibilityLabel={`View ${item.name || 'image'} ${index + 1}`}
          onPress={() => navigateTo(index)}>
          {(item as any).isImage || (item as any).isVideo ? (
            <CachedImage
              imageClientId={item.clientId}
              image={item.file}
              width={thumbnailWidth}
              height={thumbnailHeight}
              contentType={item.contentType}
              style={{
                backgroundColor: theme.colors.black,
                borderRadius: theme.spacing.xs,
              }}
            />
          ) : (
            <Box
              width={thumbnailWidth}
              height={thumbnailHeight}
              alignItems='center'
              justifyContent='center'
              borderRadius='xs'
              backgroundColor='grey01'>
              <Icon
                fileType={(item as any).contentType || undefined}
                color='blue'
                variant='xl'
                marginBottom='xxs'
              />
              <Text
                variant='labelRequired'
                numberOfLines={1}
                maxWidth={thumbnailWidth - 16}
                style={{ color: theme.colors.textPrimary }}>
                {(item as any)?.name}
              </Text>
            </Box>
          )}
        </TouchableOpacity>
      </Box>
    ) : (
      <Box
        width={thumbnailWidth}
        height={thumbnailHeight}
        justifyContent='center'
        alignItems='center'
        pointerEvents='auto'>
        <TouchableOpacity
          accessibilityLabel={`Currently viewing ${item.name || 'image'} ${
            index + 1
          }`}
          onPress={() => navigateTo(index)}>
          {(item as any).isImage || (item as any).isVideo ? (
            <CachedImage
              imageClientId={item.clientId}
              style={{
                borderColor: theme.colors.white,
                borderWidth: theme.spacing.xxxs,
                backgroundColor: theme.colors.black,
                borderRadius: theme.spacing.xs,
              }}
              image={item.file}
              contentType={item.contentType}
              width={thumbnailWidth}
              height={thumbnailHeight}
            />
          ) : (
            <Box
              width={thumbnailWidth}
              height={thumbnailHeight}
              alignItems='center'
              justifyContent='center'
              borderRadius='xs'
              style={{
                borderColor: theme.colors.white,
                borderWidth: theme.spacing.xxxs,
                backgroundColor: theme.colors.grey03,
              }}>
              <Icon
                fileType={(item as any).contentType || undefined}
                color='blue'
                variant='xl'
                marginBottom='xxs'
              />
              <Text
                variant='labelRequired'
                numberOfLines={1}
                maxWidth={thumbnailWidth - 16}
                style={{ color: theme.colors.textPrimary }}>
                {(item as any)?.name}
              </Text>
            </Box>
          )}
        </TouchableOpacity>
      </Box>
    );

  return (
    <WebModal
      accessibilityLabel='Preview'
      visible
      width={window.innerWidth}
      height={window.innerHeight}
      isFullScreen={true}>
      <>
        {showDeleteTaskFile && (
          <ConfirmModal
            showModal={showDeleteTaskFile}
            onClose={() => setShowDeleteTaskFile(false)}
            onPress={() => {
              deleteTaskFileById(taskDocs, chatId[0], () => {
                handleClose();
              });
            }}
            buttonText={t('shared:delete')}
            title={t('models:deleteTask:title')}
            message={t('models:files.deleteTaskFiles')}
          />
        )}
        {showDeleteProjectFile && (
          <ConfirmModal
            showModal={showDeleteProjectFile}
            onClose={() => setShowDeleteProjectFile(false)}
            onPress={() => {
              deleteProjectFileById(projectDocs, chatId[0] ?? '', () => {
                handleClose();
              });
            }}
            buttonText={t('shared:delete')}
            title={t('models:deleteTask:title')}
            message={t('models:files.deleteProjectFiles')}
          />
        )}
        <Box
          style={{ height: 79 }}
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
          backgroundColor='grey05'
          paddingHorizontal='m'>
          <Box
            justifyContent='flex-start'
            alignItems='center'
            flexDirection='row'
            style={{ height: 43 }}>
            <Icon
              name='ChevronLeft'
              variant='m'
              color='white'
              onPress={handleClose}
              accessibilityLabel='Go back'
            />
            <Avatar
              id={activeDoc?.owner?.id}
              avatar={activeDoc?.owner?.avatar}
              label={activeDoc?.owner?.name}
              size='small'
              marginLeft={theme.spacing.xs}
            />
            <Box style={{ height: 43 }} marginLeft='xs'>
              <Text variant='labelEmphasized' color='white' marginBottom='xxxs'>
                {activeDoc?.owner?.name}
              </Text>
              <Text variant='metadataSecondary' color='grey02'>
                {formatTime('timeAgoMessageList', {
                  val: activeDoc?.createdAt,
                }) +
                  ' at ' +
                  dayjs(activeDoc?.createdAt).format('hh:mm A')}
              </Text>
            </Box>
          </Box>

          <Box justifyContent='center'>
            <Text variant='labelEmphasized' color='white'>
              {activeDoc?.name}
            </Text>
          </Box>

          <Box
            justifyContent='flex-end'
            alignItems='center'
            flexDirection='row'
            style={{ height: 40 }}>
            {!xisViewerOnly && (
              <Box marginRight='s'>
                <WebIcon
                  isWhiteIcon={true}
                  name='Download'
                  variant='m'
                  color='white'
                  onPress={downloadImage}
                  accessibilityLabel='Download'
                />
              </Box>
            )}
            <Box marginRight='s'>
              <WebIcon
                isWhiteIcon={true}
                name='RotateCw'
                variant='m'
                color='white'
                onPress={() => rotateRight()}
                testID='rotate-button'
                accessibilityLabel='Rotate'
              />
            </Box>
            {!activeDoc?.contentType?.includes('video') && (
              <>
                <Box marginRight='s'>
                  <WebIcon
                    name='ZoomOut'
                    variant='m'
                    isWhiteIcon={imgPercentage !== MIN_PERCENTAGE}
                    color={imgPercentage == MIN_PERCENTAGE ? 'grey04' : 'white'}
                    onPress={() => {
                      const newPercentage = imgPercentage - 10;
                      if (newPercentage >= MIN_PERCENTAGE) {
                        setImgPercentage(newPercentage);
                        setOffset({ x: 0, y: 0 });
                      }
                    }}
                    accessibilityLabel='Zoom out'
                  />
                </Box>
                <Box marginRight='m'>
                  <WebIcon
                    isWhiteIcon={imgPercentage !== MIN_PERCENTAGE}
                    name='ZoomIn'
                    variant='m'
                    color={imgPercentage == MAX_PERCENTAGE ? 'grey04' : 'white'}
                    onPress={() => {
                      const newPercentage = imgPercentage + 10;
                      if (newPercentage <= MAX_PERCENTAGE) {
                        setImgPercentage(newPercentage);
                        setOffset({ x: 0, y: 0 });
                      }
                    }}
                    accessibilityLabel='Zoom in'
                  />
                </Box>
                <Text
                  variant='labelSmall'
                  color='white'
                  style={{
                    marginRight: theme.spacing.s + theme.spacing.xs,
                    minWidth: theme.spacing.xl + theme.spacing.xxs,
                  }}>
                  {imgPercentage}%
                </Text>
              </>
            )}
            {!xisViewerOnly && (
              <MediaShareModal
                fromChatId={fromChatId}
                fromChatMessage={fromChatMessage}
                isGlobalSearch={isGlobalSearch}
                onDeleteFile={() => {
                  if (deleteCall == 'project') {
                    setShowDeleteProjectFile(true);
                  } else if (deleteCall == 'task') {
                    setShowDeleteTaskFile(true);
                  }
                }}
              />
            )}
          </Box>
        </Box>
        <Box flex={1} backgroundColor='black'>
          <Box flex={1} justifyContent='center'>
            <Box justifyContent='center' alignItems='center'>
              <View
                key={
                  previewDocuments[activeIndex]?.clientId ??
                  `preview-${activeIndex}`
                }
                style={{ width: screenWidth }}>
                {previewDocuments[activeIndex] &&
                  carouselItem(previewDocuments[activeIndex])}
              </View>

              {(previewDocuments.length > 1 || canLoadMore) && (
                <>
                  {activeIndex !== 0 && (
                    <Box style={[styles.arrow, styles.leftArrow]}>
                      {supportNonImageFiles ? (
                        <TouchableOpacity
                          onPress={() => navigateTo(activeIndex - 1)}>
                          <Box style={styles.arrowIcon}>
                            <Icon
                              color='white'
                              style={{ width: 32, height: 32 }}
                              name='ChevronLeft'
                              onPress={() => navigateTo(activeIndex - 1)}
                              accessibilityLabel='Previous image'
                            />
                          </Box>
                        </TouchableOpacity>
                      ) : (
                        <WebIcon
                          isWhiteIcon={true}
                          color='white'
                          style={{ width: 42, height: 42 }}
                          name='ArrowLeftCircle'
                          onPress={() => navigateTo(activeIndex - 1)}
                          accessibilityLabel='Previous image'
                        />
                      )}
                    </Box>
                  )}
                  {(activeIndex !== previewDocuments.length - 1 ||
                    canLoadMore) && (
                    <Box style={[styles.arrow, styles.rightArrow]}>
                      {supportNonImageFiles ? (
                        <TouchableOpacity
                          onPress={() => navigateTo(activeIndex + 1)}>
                          <Box style={styles.arrowIcon}>
                            {pendingAdvance ? (
                              <ActivityIndicator
                                color={theme.colors.white}
                                accessibilityLabel='Loading more files'
                              />
                            ) : (
                              <Icon
                                color='white'
                                style={{ width: 32, height: 32 }}
                                name='ChevronRight'
                                onPress={() => navigateTo(activeIndex + 1)}
                                accessibilityLabel='Next image'
                              />
                            )}
                          </Box>
                        </TouchableOpacity>
                      ) : (
                        <WebIcon
                          isWhiteIcon={true}
                          color='white'
                          style={{ width: 42, height: 42 }}
                          name='ArrowRightCircle'
                          onPress={() => navigateTo(activeIndex + 1)}
                          accessibilityLabel='Next image'
                        />
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
          {supportNonImageFiles ? (
            <>
              <Box
                backgroundColor='grey05'
                justifyContent='center'
                alignItems='center'
                style={{
                  paddingVertical: tags.length > 0 ? 16 : 12,
                }}>
                {tags.length > 0 && (
                  <FlatList
                    horizontal
                    data={tags}
                    renderItem={({ item, index }) => (
                      <Box key={index}>
                        <TagsCollection
                          fromImagesPreview={true}
                          isImagesPreview={true}
                          tagsCollection={item}
                          showAuthor={false}
                          borderless
                          isRow
                        />
                      </Box>
                    )}
                    ItemSeparatorComponent={MarginRightM}
                  />
                )}
              </Box>
              <Box
                width={screenWidth}
                backgroundColor='grey05'
                alignItems={
                  previewDocuments.length * thumbnailWidth +
                    (previewDocuments.length + 1) * 16 >
                  screenWidth
                    ? undefined
                    : 'center'
                }>
                <FlatList
                  horizontal
                  data={previewDocuments}
                  keyExtractor={(item) => item.id ?? item.clientId}
                  renderItem={renderThumbnailItem}
                  scrollEnabled
                  keyboardShouldPersistTaps='handled'
                  showsHorizontalScrollIndicator
                  // Fetch the next page when the strip itself is scrolled to
                  // its end, so reaching the trailing loading cell actually
                  // loads more instead of waiting for arrow navigation
                  // (TA-9771). requestMore's same-length guard absorbs
                  // repeated end-reached events.
                  onEndReached={() => requestMore()}
                  onEndReachedThreshold={0.5}
                  testID='images-preview-thumb-strip'
                  contentContainerStyle={{
                    paddingHorizontal: theme.spacing.m,
                  }}
                  ListFooterComponent={
                    canLoadMore ? (
                      <Box
                        width={thumbnailWidth}
                        height={thumbnailHeight}
                        marginLeft='m'
                        justifyContent='center'
                        alignItems='center'
                        testID='thumb-strip-loading-more'>
                        <ActivityIndicator color={theme.colors.white} />
                      </Box>
                    ) : null
                  }
                  ItemSeparatorComponent={MarginRightM}
                  style={{
                    overflowX: 'auto' as any,
                    overflowY: 'hidden' as any,
                    paddingBottom: theme.spacing.l,
                  }}
                />
              </Box>
            </>
          ) : (
            <>
              <Box justifyContent='center' alignItems='center'>
                <Box
                  style={{
                    height: 62,
                    marginBottom: 42,
                    width:
                      previewDocuments.length * 36 +
                      (previewDocuments.length + 1) * 8,
                    backgroundColor: '#222222',
                  }}
                  borderRadius='xxs'
                  flexDirection='row'
                  justifyContent='space-between'
                  paddingHorizontal='xs'
                  paddingVertical='xs'>
                  {previewDocuments.map((item, index) => {
                    return activeIndex !== index ? (
                      <Box
                        width='xl'
                        height='xxl'
                        justifyContent='center'
                        alignItems='center'
                        pointerEvents='auto'>
                        <TouchableOpacity
                          accessibilityLabel={`View ${item.name || 'image'} ${
                            index + 1
                          }`}
                          onPress={() => navigateTo(index)}>
                          <CachedImage
                            imageClientId={item.clientId}
                            image={item.file}
                            width={theme.spacing.xl}
                            height={theme.spacing.xxl}
                            contentType={item.contentType}
                            style={{ backgroundColor: theme.colors.black }}
                          />
                        </TouchableOpacity>
                      </Box>
                    ) : (
                      <Box
                        width='xl'
                        height='xxl'
                        justifyContent='center'
                        alignItems='center'
                        pointerEvents='auto'>
                        <TouchableOpacity
                          accessibilityLabel={`Currently viewing ${
                            item.name || 'image'
                          } ${index + 1}`}
                          onPress={() => navigateTo(index)}>
                          <CachedImage
                            imageClientId={item.clientId}
                            style={{
                              borderColor: theme.colors.greenSecondary,
                              borderWidth: theme.spacing.xxxs,
                              backgroundColor: theme.colors.black,
                            }}
                            image={item.file}
                            width={theme.spacing.xl}
                            contentType={item.contentType}
                            height={theme.spacing.xxl}
                          />
                        </TouchableOpacity>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Box
                backgroundColor='grey05'
                justifyContent='center'
                alignItems='center'
                style={{
                  paddingBottom: 0,
                  height: 54,
                }}>
                {tags.length > 0 && (
                  <FlatList
                    horizontal
                    style={{
                      position: 'absolute',
                      top: 1,
                      backgroundColor: theme.colors.grey05,
                      paddingVertical: theme.spacing.xxs,
                    }}
                    data={tags}
                    renderItem={({ item, index }) => (
                      <Box key={index}>
                        <TagsCollection
                          fromImagesPreview={true}
                          tagsCollection={item}
                          showAuthor={false}
                          borderless
                          isRow
                        />
                      </Box>
                    )}
                    ItemSeparatorComponent={MarginRightXxs}
                    ListHeaderComponent={MarginLeftM}
                    ListFooterComponent={MarginLeftM}
                  />
                )}
              </Box>
            </>
          )}
        </Box>
      </>
    </WebModal>
  );
};

const styles = StyleSheet.create({
  arrow: {
    position: 'absolute',
    top: `50%`,
    zIndex: 99,
  },
  arrowIcon: {
    width: 64,
    height: 64,
    backgroundColor: 'rgba(247, 248, 250, 0.25)',
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rightArrow: {
    right: theme.spacing.m,
  },
  leftArrow: {
    left: theme.spacing.m,
  },
});
